import {
  assessmentFilterTypes,
  deserify,
  DocumentViewer,
  getLocalStorage,
  getMediaBasePath,
  IClassAndSubjectSelected,
  IStyles,
  pxToRem,
  pxTovW,
  SearchBar,
  SelectMenu,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';

import { useEffect, useState } from 'react';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useDispatch } from 'react-redux';
import { FileEnum } from '@protos/content_management/content.db_pb';

import { Pagination, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { ReportTable } from './ReportTable';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { setClassAndSubjectInfo } from '../Home/reducer/homeDashboard.slice';
import {
  resetSelectedReportChapter,
  resetSelectedReportTopic,
  setCourseIndexInfo,
  setPageNumber,
  setReportsData,
  setSearchByAssessType,
  setSelectedChapter,
  setSelectedTopic,
} from './reducer/reports.slice';
import { setToastInfo } from '../Home/reducer/homeDashboard.slice';
import {
  TeacherAssessmentReportData,
  TeacherReportCenterData,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { interactionEvent } from '../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {
  root: {},
  tableContainer: {
    border: '1px solid #E8E8E8',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    mt: { xs: pxToRem(25), md: pxTovW(25) },
    mb: { xs: pxToRem(20), md: pxTovW(50) },
  },
  headerRow: {
    backgroundColor: '#E8E8E8',
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(10)}`,
      md: ` ${pxTovW(0)} ${pxTovW(20)}`,
    },
    boxSizing: 'border-box',
  },
  headerCell: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(10)} ${pxTovW(20)} ${pxTovW(0)}`,
      lg: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
  bodyCells: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
  twoLineClamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    margin: 'auto',
  },
  checkbox: {
    '& .MuiSvgIcon-root': {
      fontSize: { xs: pxToRem(35), md: pxTovW(40) },
    },
    '&.Mui-checked': {
      color: '#0AA34F',
    },
    padding: 0,
    color: '#E8E8E8',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(6), md: pxTovW(12) },
  },
  pagination: {
    width: 'max-content',
    margin: 'auto',
    mt: { xs: pxToRem(20), md: pxTovW(30) },
  },
};

export const Reports = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  // const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | undefined
  >(undefined);
  const [selectedReportInfo, setSelectedReportInfo] = useState<{
    subject?: string;
    class?: string;
    section?: string;
    assessmentName?: string;
  }>({});
  const {
    selected_chapter,
    chapter_topic_data,
    selected_topic,
    reportData,
    searchByAssessType,
    pageNumber,
  } = deserify(useAppSelector((state) => state.report));
  const { class_subject_info } = useAppSelector((state) => state.homeDashboard);
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const [searchText, setSearchText] = useState<string>('');
  const {
    ContentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherReportsAPIServiceV1ClientWithStatusCodeHandler,
    SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const teacher_id = getLocalStorage('userId');
  const [selectedclassandsubject, setSelectedclassandsubject] = useState(
    (class_subject_info?.classname ?? '') +
      (class_subject_info?.section ?? '') +
      ' ' +
      (class_subject_info?.subject ?? '')
  );
  const [allSubjects, setAllSubjects] = useState<IClassAndSubjectSelected[]>(
    []
  );
  const [selectedsubjectId, setSelectedSubjectId] = useState(
    class_subject_info?.subjectId
  );

  useEffect(() => {
    if (user_info?.teachClassSubjects?.length) {
      const arr = user_info?.teachClassSubjects.flatMap((classInfo) =>
        classInfo.subjects.map((subject) => ({
          classname: classInfo.className,
          section: classInfo.sectionName,
          sectionId: classInfo.sectionId,
          subjectId: subject.subjectId,
          subject: subject.subjectName,
          icon: subject.iconUrl
            ? getMediaBasePath(subject.iconUrl, 'schoolnetStaticAssetBucket')
            : '',
          color: subject.textColor,
          bookId: subject.bookId,
          classId: classInfo.classId,
          isClickerSupported: classInfo.isClickerSupported ? true : false,
        }))
      );
      setAllSubjects([...arr]);
    }
  }, [user_info?.teachClassSubjects?.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const chapterTopicData =
          await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchCourseIndexInfo(
            {
              personId: BigInt(teacher_id),
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              subjectId: Number(selectedsubjectId),
            }
          );
        if (chapterTopicData) {
          dispatch(setCourseIndexInfo(chapterTopicData?.data));
        }
      } catch (error) {
        dispatch(
          setToastInfo({
            label: 'Error fetching Chapter Topic Data',
            variant: 'error',
            open: true,
          })
        );
      } finally {
        // setLoading(false); // Stop loading
      }
    };
    fetchData();
  }, [selectedsubjectId]);

  useEffect(() => {
    fetchReport(pageNumber, searchText);
  }, [
    selected_chapter?.chapterId,
    selected_topic?.topicId,
    class_subject_info?.subjectId,
    class_subject_info?.sectionId,
    searchByAssessType?.id,
  ]);

  const fetchReport = async (pageNo: number, search?: string) => {
    setLoading('loading');
    try {
      const reponse =
        await LmsAssessmentTeacherReportsAPIServiceV1ClientWithStatusCodeHandler.teacherAssessmentReportsResponse(
          {
            teacherId: teacher_id,
            pageNumber: pageNo,
            subjectId: class_subject_info?.subjectId,
            sectionId: class_subject_info?.sectionId,
            chapterId: selected_chapter?.chapterId,
            topicId: selected_topic?.topicId,
            assessmentType: searchByAssessType?.id,
            searchKey: search ? search?.trim() : undefined,
          }
        );
      if (reponse.data) {
        calculatePaginationInfo(reponse.data);

        setLoading('completed');
        dispatch(setReportsData(reponse.data));
      } else {
        setLoading('error');
        dispatch(
          setToastInfo({
            label: 'Something went wrong',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      console.log(err, 'error');
      setLoading('error');
      dispatch(
        setToastInfo({
          label: 'Something went wrong',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const headers = [
    { name: 'ASSIGNED DATE', cellStyle: styles.headerCell },
    { name: 'ASSESSMENT NAME', cellStyle: styles.headerCell },
    { name: 'ASSESSMENT TYPE', cellStyle: styles.headerCell },
    { name: 'CLASS', cellStyle: styles.headerCell },
    { name: 'SECTION', cellStyle: styles.headerCell },
    { name: 'SUBJECT', cellStyle: styles.headerCell },
    { name: 'EXAM SCOPE', cellStyle: styles.headerCell },
    { name: 'TOTAL SUBMISSIONS', cellStyle: styles.headerCell },
    { name: 'ACTION', cellStyle: styles.headerCell },
  ];

  const genrateReports = async (row: TeacherReportCenterData) => {
    if (!teacher_id) return;
    setLoading('loading');
    try {
      const response =
        await SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler.fetchSchoolAssessmentReport(
          {
            assessmentId: row.assessmentId
              ? Number(row.assessmentId)
              : undefined,
            teacherId: teacher_id,
          }
        );
      if (response) {
        if (
          !response.data?.gcpReportUrl ||
          response.data?.gcpReportUrl === 'null'
        ) {
          dispatch(
            setToastInfo({
              label: response?.message
                ? `${response?.message.replace(/\[.*?\]/g, '')}`
                : 'Assessment report Generation in Progress',
              variant: 'success',
              open: true,
            })
          );
          setLoading('completed');
          return;
        }
        fetchReport(pageNumber, searchText);
      }
      // setPdfUrl(response.data?.gcpReportUrl);
      setPdfUrl(getMediaBasePath(response.data?.gcpReportUrl));
      setSelectedAssessmentId(row.assessmentId);
      setSelectedReportInfo({
        subject: row.subject,
        class: row.class,
        section: row.section,
        assessmentName: row.assessmentName,
      });
      setLoading('completed');
    } catch (error) {
      console.log(error, 'error');
      setLoading('completed');
      dispatch(
        setToastInfo({
          label: 'Error in genrating report',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const calculatePaginationInfo = (
    paginationData: TeacherAssessmentReportData
  ) => {
    const countInPagination =
      Number(paginationData?.teacherReportsInfo?.countInPagination) || 1;
    const reportsCount =
      Number(paginationData?.teacherReportsInfo?.reportCount) || 0;

    const totalPages = Math.max(1, Math.ceil(reportsCount / countInPagination));

    // setPageNumber(1); // Reset to first page on search
    setTotalPages(totalPages);
  };

  const handleSearch = (search: string) => {
    dispatch(
      setPageNumber(1) // Reset to first page on search
    );
    fetchReport(1, search); // API call
  };

  // const handleSearchTextChange = (value: string | null) => {
  //   setSearchText(value);
  //   if (value === '' || value === null) {
  //     fetchReport(); // Trigger fetch when cleared
  //   }
  // };

  return (
    <Box>
      <Box
        sx={{
          padding: {
            xs: `${pxToRem(10)} ${pxToRem(20)} ${pxToRem(10)} ${pxToRem(20)}`,
            md: ` ${pxTovW(27)} ${pxTovW(50)}`,
            lg: ` ${pxTovW(30)} ${pxTovW(230)}`,
          },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 'bold',
          }}
        >
          Report Center
        </Typography>
      </Box>

      <Box
        sx={{
          padding: {
            xs: `${pxToRem(0)} ${pxToRem(20)} ${pxToRem(10)} ${pxToRem(20)}`,
            md: ` ${pxTovW(27)} ${pxTovW(50)}`,
            lg: ` ${pxTovW(10)} ${pxTovW(130)}`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: {
              xs: 'space-between',
              md: 'space-between',
              lg: 'center',
            },
            gap: { xs: pxToRem(10), md: pxTovW(15), lg: pxTovW(10) },
            // marginLeft: { lg: pxTovW(60) },
            mt: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: 'max-content', md: pxTovW(550), lg: pxTovW(474) },
              marginTop: { xs: pxToRem(15), md: pxTovW(38), lg: pxTovW(34) },
              // marginRight: { xs: pxToRem(10), md: pxToRem(100) },
            }}
          >
            <SearchBar
              searchText={searchText}
              handleSearchTextChange={(value) => {
                setSearchText(value);
                if (value === '' || value === null) {
                  fetchReport(1); // Trigger fetch when cleared
                }
              }}
              handleSearch={async () => {
                handleSearch(searchText);
                await interactionEvent({
                  context: 'Navigation_bar',
                  name: 'SEARCH',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              handleClose={() => {
                setSearchText('');
                handleSearch('');
                // getTeacherList('');
              }}
            />
          </Box>

          <Box sx={styles.selectContainer}>
            <Typography variant="h4">Choose Class & Subject</Typography>
            <SelectMenu
              placeholder="Select"
              value={selectedclassandsubject}
              onChange={async (value: string) => {
                setSelectedclassandsubject(value);
                const classSubjectObj = allSubjects?.find(
                  (e) => e.classname + e.section + ' ' + e.subject === value
                );
                if (classSubjectObj) {
                  setSelectedSubjectId(classSubjectObj?.subjectId);
                }
                dispatch(setPageNumber(1)); // Reset to first page on search
                dispatch(setClassAndSubjectInfo(classSubjectObj));
                dispatch(resetSelectedReportChapter());
                dispatch(resetSelectedReportTopic());
                // setSearchText('');
                await interactionEvent({
                  context: 'Choose_Class_&_Subject',
                  name: value.toUpperCase().replace(/\s+/g, '_'),
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              optionList={
                allSubjects?.map((e) => {
                  return {
                    name: e.classname + e.section + ' ' + e.subject,
                    id: e.subjectId,
                  };
                }) || []
              }
              rootStyles={{
                width: { xs: pxToRem(152), md: pxTovW(540), lg: pxTovW(230) },
                height: { xs: pxToRem(30), md: pxTovW(75), lg: pxTovW(56) },
              }}
              // disabled={true}
            />
          </Box>
          <Box sx={styles.selectContainer}>
            <Typography variant="h4">Select Chapter</Typography>
            <SelectMenu
              placeholder="Select"
              value={
                selected_chapter?.chapterTitle
                  ? `${selected_chapter.chapterNo}. ${selected_chapter.chapterTitle}`
                  : ''
              }
              clearIcon={true}
              onChange={async (value: string) => {
                // Find the chapter object by matching the selected title
                if (!value) {
                  dispatch(resetSelectedReportChapter());
                  dispatch(resetSelectedReportTopic());
                } else {
                  const selectedChapterObj = chapter_topic_data?.chapters?.find(
                    (chapter) =>
                      `${chapter.chapterNo}. ${chapter.chapterTitle}` === value
                  );
                  dispatch(resetSelectedReportTopic());
                  if (selectedChapterObj) {
                    const chapterData = {
                      chapterTitle: selectedChapterObj.chapterTitle,
                      chapterId: selectedChapterObj.chapterId,
                      chapterNo: selectedChapterObj.chapterNo,
                    };
                    dispatch(setPageNumber(1)); // Reset to first page on search
                    dispatch(setSelectedChapter(chapterData));
                    // setSearchText('');
                  }
                }
                if (value.trim() !== '') {
                  await interactionEvent({
                    context: 'Select_Chapter',
                    name: value.toUpperCase().replace(/\s+/g, '_'),
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }
              }}
              optionList={
                chapter_topic_data?.chapters?.map((chapter) => {
                  return {
                    name: `${chapter.chapterNo}. ${chapter.chapterTitle}`,
                    id: chapter.chapterId,
                  };
                }) || []
              }
              rootStyles={{
                width: { xs: pxToRem(152), md: pxTovW(540), lg: pxTovW(280) },
                height: { xs: pxToRem(30), md: pxTovW(75), lg: pxTovW(56) },
              }}
            />
          </Box>
          <Box sx={styles.selectContainer}>
            <Typography variant="h4">Select Topic</Typography>
            <SelectMenu
              placeholder="Select"
              value={
                selected_topic?.topicTitle ? selected_topic?.topicTitle : ''
              }
              disabled={selected_chapter ? false : true}
              clearIcon={true}
              onChange={async (value: string) => {
                setSelectedTopic(value);
                if (!value) {
                  dispatch(resetSelectedReportTopic());
                } else {
                  const selectedTopicObj = chapter_topic_data?.chapters
                    .find(
                      (chapter) =>
                        Number(chapter.chapterId) ===
                        Number(selected_chapter?.chapterId)
                    )
                    ?.topics?.find((topic) => topic.topicTitle === value);
                  if (selectedTopicObj) {
                    const topicData = {
                      topicTitle: selectedTopicObj.topicTitle,
                      topicId: selectedTopicObj.topicId,
                    };
                    dispatch(setSelectedTopic(topicData));
                    dispatch(setPageNumber(1));
                  }
                }
                if (value.trim() !== '') {
                  await interactionEvent({
                    context: 'Select_Topic',
                    name: value.toUpperCase().replace(/\s+/g, '_'),
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }
              }}
              optionList={
                chapter_topic_data?.chapters
                  ?.find((chapter) => {
                    return (
                      Number(chapter.chapterId) ===
                      Number(selected_chapter?.chapterId)
                    );
                  })
                  ?.topics?.map((topic) => {
                    return {
                      name: topic.topicTitle,
                      id: topic.topicId,
                    };
                  }) || []
              }
              rootStyles={{
                width: { xs: pxToRem(152), md: pxTovW(540), lg: pxTovW(280) },
                height: { xs: pxToRem(30), md: pxTovW(75), lg: pxTovW(56) },
              }}
            />
          </Box>
          <Box sx={styles.selectContainer}>
            <Typography variant="h4">Choose Assessment Type</Typography>
            <SelectMenu
              placeholder="Select"
              value={searchByAssessType ? searchByAssessType.name : ''}
              onChange={async (value: string) => {
                const selectedAssessmentType = assessmentFilterTypes.find(
                  (option) => option.name === value
                );
                dispatch(setPageNumber(1)); // Reset to first page on search
                dispatch(setSearchByAssessType(selectedAssessmentType));
                if (value.trim() !== '') {
                  await interactionEvent({
                    context: 'Choose_Assessment_Type',
                    name: value.toUpperCase().replace(/\s+/g, '_'),
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }
              }}
              optionList={assessmentFilterTypes.filter(
                (option) => option.name !== 'Question Paper'
              )}
              rootStyles={{
                width: { xs: pxToRem(152), md: pxTovW(540), lg: pxTovW(230) },
                height: { xs: pxToRem(30), md: pxTovW(75), lg: pxTovW(56) },
              }}
              clearIcon={true}
            />
          </Box>
        </Box>

        <ReportTable
          headers={headers}
          loading={loading}
          genrateReports={genrateReports}
          icon="report-icon"
        />

        {(reportData?.teacherReports ?? []).length > 0 && (
          <Pagination
            page={pageNumber}
            count={totalPages}
            color="secondary"
            onChange={async (
              event: React.ChangeEvent<unknown>,
              page: number
            ) => {
              dispatch(setPageNumber(page));
              fetchReport(page, searchText);
            }}
            sx={styles.pagination}
          />
        )}

        {pdfUrl && (
          <DocumentViewer
            downloadUrl={pdfUrl}
            previewUrl={pdfUrl}
            gcpUrl={pdfUrl}
            type={FileEnum.FILE_TYPE_PDF}
            fileName={
              selectedReportInfo.subject ||
              selectedReportInfo.class ||
              selectedReportInfo.section
                ? `${selectedReportInfo.subject}_${selectedReportInfo.class}_${
                    selectedReportInfo.section
                  }_${
                    selectedReportInfo.assessmentName || selectedAssessmentId
                  }_Geneo_Assessment_Report_Teacher.pdf`
                : 'Geneo_Assessment_Report_Teacher.pdf'
            }
            open={!!pdfUrl}
            closeFile={() => {
              setPdfUrl(undefined);
              setSelectedAssessmentId(undefined);
              setSelectedReportInfo({});
            }}
          />
        )}
      </Box>
    </Box>
  );
};
